import { formatDate } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { propertyKeys } from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';

interface TrashItem {
  id: string;
  code: string;
  title: string;
  assignedTo: string;
  deletedAt: string;
  purgeAt: string;
}

export function PropertiesTrashPage() {
  const { data: session } = useSession();
  const client = useQueryClient();
  const notify = useToast();
  const trash = useQuery({
    queryKey: propertyKeys.trash,
    queryFn: () => api<TrashItem[]>('/properties/trash'),
  });
  const restore = async (item: TrashItem) => {
    try {
      await api(`/properties/${item.id}/restore`, { method: 'POST' });
      await client.invalidateQueries({ queryKey: propertyKeys.all });
      notify(`«${item.title}» se restauró.`, 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const tz = session?.tenant.timezone;
  return (
    <div className="space-y-5">
      <div>
        <Link to="/propiedades" className="text-sm text-primary hover:underline">
          ← Propiedades
        </Link>
        <h1 className="text-2xl font-semibold">Papelera de propiedades</h1>
        <p className="text-sm text-muted-foreground">
          Se pueden restaurar durante 30 días; después se borran para siempre.
        </p>
      </div>
      {trash.isPending ? (
        <Loading />
      ) : trash.error ? (
        <Alert tone="danger">{errorMessage(trash.error)}</Alert>
      ) : trash.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">La papelera está vacía.</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border bg-surface">
          {trash.data.map((item) => (
            <li
              key={item.id}
              className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:p-4"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {item.title}{' '}
                  <span className="font-mono text-sm text-muted-foreground">{item.code}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {item.assignedTo} · Eliminada el {formatDate(item.deletedAt, tz)} · Se borra el{' '}
                  {formatDate(item.purgeAt, tz)}
                </p>
              </div>
              <Button size="sm" variant="secondary" onClick={() => void restore(item)}>
                Restaurar
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
